import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ScrollText, ShieldCheck, Plus, Save, Trash2, X, ArrowRight, ArrowLeft, Power, PowerOff, GripVertical,
} from "lucide-react";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBreakpoint } from "@/lib/useBreakpoint";

/**
 * AdminPoliciesPage — STAS-only. Manages the office policies collection.
 * Route: /admin/policies
 *
 * Displays each policy with: title, body (AR + EN), tone, priority, active,
 * and location toggles (invoice_footer / refund_confirm / portal_footer / …).
 */
const LOCATION_LABELS = {
  invoice_footer:            { ar: "أسفل الفاتورة",              en: "Invoice footer" },
  invoice_before_issue:      { ar: "قبل إصدار الفاتورة",         en: "Before issuing invoice" },
  portal_footer:             { ar: "أسفل البوابة",                en: "Portal footer" },
  portal_landing:            { ar: "الصفحة الرئيسية للبوابة",     en: "Portal landing" },
  receipt_footer:            { ar: "أسفل الإيصال",                en: "Receipt footer" },
  application_detail:        { ar: "تفاصيل المعاملة",             en: "Application detail" },
  office_submission_confirm: { ar: "قبل التقديم الداخلي",         en: "Before office submit" },
  refund_confirm:            { ar: "قبل الاسترداد",               en: "Before refund" },
  cancel_confirm:            { ar: "قبل إلغاء الفاتورة",          en: "Before cancel" },
};

const TONE_LABELS = {
  info:    { ar: "معلومة",  en: "Info",    color: "#3B82F6" },
  warning: { ar: "تحذير",   en: "Warning", color: "#D97706" },
  danger:  { ar: "خطر",     en: "Danger",  color: "#DC2626" },
  success: { ar: "إيجابي", en: "Success", color: "#16A34A" },
};

const emptyDraft = () => ({
  key: "",
  title_ar: "",
  title_en: "",
  body_ar: "",
  body_en: "",
  locations: [],
  is_active: true,
  priority: 100,
  tone: "info",
});

export default function AdminPoliciesPage() {
  const { theme, lang, dir } = useI18n();
  const { user } = useAuth();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const isRoot = !!user?.role?.is_root;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/policies");
      setItems(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "تعذّر التحميل" : "Load failed"));
    } finally { setLoading(false); }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  if (!isRoot) {
    return (
      <div style={{ padding: 40, color: theme.textMuted, textAlign: "center" }}>
        {lang === "ar" ? "هذه الصفحة لـ STAS فقط." : "STAS only."}
      </div>
    );
  }

  const save = async () => {
    if (!editing) return;
    if (!editing.key.trim() || !editing.title_ar.trim() || !editing.body_ar.trim()) {
      alert(lang === "ar" ? "المفتاح والعنوان العربي والمحتوى العربي مطلوبة" : "Key, Arabic title & Arabic body are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.id) {
        await api.put(`/admin/policies/${editing.id}`, payload);
      } else {
        await api.post("/admin/policies", payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || (lang === "ar" ? "تعذّر الحفظ" : "Save failed"));
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(lang === "ar" ? `حذف السياسة "${p.title_ar}"؟` : `Delete "${p.title_en || p.title_ar}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/policies/${p.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || (lang === "ar" ? "لا يمكن الحذف" : "Cannot delete"));
    } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    setBusy(true);
    try {
      await api.put(`/admin/policies/${p.id}`, { ...p, is_active: !p.is_active });
      await load();
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const T = lang === "ar" ? {
    title: "السياسات الرسمية",
    subtitle: "تحكّم مركزي بالسياسات (STAS فقط). تظهر تلقائياً في المواضع التي تختارينها.",
    add: "سياسة جديدة",
    empty: "لا توجد سياسات بعد",
    back: "رجوع",
    body_ph: "اكتبي نص السياسة الرسمي — يظهر كما هو للعميل/الموظف",
  } : {
    title: "Official Policies",
    subtitle: "Central control (STAS only). Policies appear automatically in the locations you enable.",
    add: "New policy",
    empty: "No policies yet",
    back: "Back",
    body_ph: "Write the official policy text — displayed verbatim to clients/staff",
  };

  const css = {
    page: {
      minHeight: "100%", background: theme.bg, color: theme.text,
      padding: `${isDesktop ? 28 : 16}px ${isDesktop ? 36 : 16}px 40px`,
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
      maxWidth: 1200, margin: "0 auto",
    },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" },
    title: { fontSize: isDesktop ? 30 : 24, fontWeight: 800, color: theme.text, letterSpacing: "-0.4px", display: "flex", alignItems: "center", gap: 10 },
    subtitle: { fontSize: 13, color: theme.textMuted, marginBottom: 22, lineHeight: 1.6 },
    addBtn: {
      background: theme.goldGradient, color: "#0A0F19", border: "none",
      borderRadius: 999, padding: "10px 18px", fontWeight: 800, fontSize: 13, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6, minHeight: 40, fontFamily: "inherit",
      boxShadow: "0 4px 12px rgba(212,175,55,0.25)",
    },
    backBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
    },
    grid: { display: "grid", gridTemplateColumns: isDesktop ? "repeat(auto-fill, minmax(360px, 1fr))" : "1fr", gap: 14 },
    card: (active) => ({
      background: theme.surface, border: `1px solid ${active ? theme.borderSoft : "rgba(120,120,120,0.28)"}`,
      borderRadius: 16, padding: 16, opacity: active ? 1 : 0.65, position: "relative",
    }),
    cardTitle: { fontSize: 14, fontWeight: 800, color: theme.text, marginBottom: 4, letterSpacing: "-0.1px" },
    cardKey: { fontSize: 10, color: theme.textMuted, fontFamily: "SF Mono, ui-monospace, monospace", marginBottom: 8, direction: "ltr" },
    cardBody: { fontSize: 12, color: theme.textSoft, lineHeight: 1.65, whiteSpace: "pre-wrap", marginBottom: 10, maxHeight: 120, overflow: "hidden", position: "relative" },
    locChips: { display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 },
    locChip: { fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: theme.surfaceElevated, color: theme.textSoft, border: `1px solid ${theme.borderSoft}` },
    actions: { display: "flex", gap: 6, borderTop: `1px solid ${theme.borderSoft}`, paddingTop: 10 },
    actBtn: (color) => ({
      padding: "6px 10px", borderRadius: 8, background: "transparent",
      color: color || theme.text, border: `1px solid ${color ? color + "55" : theme.border}`,
      fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: "inherit",
    }),
    modalOv: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.60)", backdropFilter: "blur(8px)",
      zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "40px 16px", overflowY: "auto",
    },
    modal: {
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 18, width: "100%", maxWidth: 640,
      boxShadow: theme.sheetShadow || "0 20px 60px rgba(0,0,0,0.35)",
      fontFamily: "inherit",
    },
    modalHead: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: `1px solid ${theme.borderSoft}` },
    modalBody: { padding: 20, display: "flex", flexDirection: "column", gap: 12 },
    fieldLbl: { fontSize: 11.5, fontWeight: 700, color: theme.textMuted, marginBottom: 4 },
    input: {
      padding: "10px 12px", borderRadius: 10, background: theme.inputBg,
      border: `1px solid ${theme.inputBorder}`, color: theme.text, fontSize: 13,
      fontFamily: "inherit", outline: "none", width: "100%", boxSizing: "border-box",
    },
    textarea: {
      padding: "10px 12px", borderRadius: 10, background: theme.inputBg,
      border: `1px solid ${theme.inputBorder}`, color: theme.text, fontSize: 13,
      fontFamily: "inherit", outline: "none", width: "100%", boxSizing: "border-box",
      minHeight: 110, resize: "vertical", lineHeight: 1.6,
    },
    locGrid: { display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "repeat(2, 1fr)", gap: 6 },
    locOpt: (on) => ({
      padding: "8px 10px", borderRadius: 10,
      background: on ? "rgba(212,175,55,0.14)" : "transparent",
      border: `1px solid ${on ? theme.borderAccent : theme.border}`,
      color: on ? theme.gold : theme.text,
      fontSize: 11.5, fontWeight: 700, cursor: "pointer", textAlign: "start", fontFamily: "inherit",
    }),
    footer: { display: "flex", gap: 8, justifyContent: "flex-end", padding: "12px 20px", borderTop: `1px solid ${theme.borderSoft}` },
  };

  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;
  const canDelete = editing?.key !== "refund_10_percent";

  return (
    <div style={css.page} dir={dir} data-testid="policies-page">
      <div style={css.header}>
        <div>
          <div style={css.title}>
            <ScrollText size={26} strokeWidth={2} color={theme.gold} />
            {T.title}
          </div>
          <div style={css.subtitle}>{T.subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => navigate(-1)} style={css.backBtn} data-testid="policies-back">
            <BackIcon size={14} strokeWidth={2} /> {T.back}
          </button>
          <button
            type="button"
            onClick={() => setEditing(emptyDraft())}
            style={css.addBtn}
            data-testid="policies-add"
          >
            <Plus size={16} strokeWidth={2.4} /> {T.add}
          </button>
        </div>
      </div>

      {err && <div style={{ padding: 12, marginBottom: 12, background: theme.errorBg, border: `1px solid ${theme.errorBorder}`, color: theme.errorText, borderRadius: 12, fontSize: 13 }}>{err}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: theme.textMuted }}>…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: theme.textMuted }}>{T.empty}</div>
      ) : (
        <div style={css.grid}>
          {items.map((p) => (
            <div key={p.id} style={css.card(p.is_active)} data-testid={`policy-card-${p.key}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={css.cardTitle}>{lang === "ar" ? p.title_ar : (p.title_en || p.title_ar)}</div>
                  <div style={css.cardKey}>{p.key}</div>
                </div>
                <span style={{
                  fontSize: 9.5, fontWeight: 800, padding: "3px 7px", borderRadius: 999,
                  background: (TONE_LABELS[p.tone]?.color || "#3B82F6") + "1F",
                  color: TONE_LABELS[p.tone]?.color || "#3B82F6",
                }}>{TONE_LABELS[p.tone]?.[lang] || p.tone}</span>
              </div>
              <div style={css.cardBody}>{lang === "ar" ? p.body_ar : (p.body_en || p.body_ar)}</div>
              <div style={css.locChips}>
                {(p.locations || []).map((loc) => (
                  <span key={loc} style={css.locChip}>{LOCATION_LABELS[loc]?.[lang] || loc}</span>
                ))}
              </div>
              <div style={css.actions}>
                <button type="button" onClick={() => setEditing(p)} style={css.actBtn(theme.gold)} data-testid={`policy-edit-${p.key}`}>
                  {lang === "ar" ? "تعديل" : "Edit"}
                </button>
                <button type="button" onClick={() => toggleActive(p)} style={css.actBtn(p.is_active ? "#16a34a" : theme.textMuted)} data-testid={`policy-toggle-${p.key}`} disabled={busy}>
                  {p.is_active ? <Power size={12} strokeWidth={2.2} /> : <PowerOff size={12} strokeWidth={2.2} />}
                  {p.is_active ? (lang === "ar" ? "مفعّلة" : "Active") : (lang === "ar" ? "معطّلة" : "Off")}
                </button>
                <div style={{ flex: 1 }} />
                {p.key !== "refund_10_percent" && (
                  <button type="button" onClick={() => remove(p)} style={css.actBtn("#dc2626")} data-testid={`policy-delete-${p.key}`} disabled={busy}>
                    <Trash2 size={12} strokeWidth={2.2} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div style={css.modalOv} onClick={() => !busy && setEditing(null)} data-testid="policy-modal">
          <div style={css.modal} onClick={(e) => e.stopPropagation()} dir={dir}>
            <div style={css.modalHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ScrollText size={20} strokeWidth={2} color={theme.gold} />
                <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>
                  {editing.id
                    ? (lang === "ar" ? "تعديل السياسة" : "Edit Policy")
                    : (lang === "ar" ? "سياسة جديدة" : "New Policy")}
                </div>
              </div>
              <button type="button" onClick={() => setEditing(null)} style={{ width: 32, height: 32, borderRadius: 10, background: "transparent", border: `1px solid ${theme.borderSoft}`, color: theme.text, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }} disabled={busy} data-testid="policy-modal-close">
                <X size={16} strokeWidth={2} />
              </button>
            </div>

            <div style={css.modalBody}>
              <div>
                <div style={css.fieldLbl}>{lang === "ar" ? "مفتاح تعريفي (فريد)" : "Key (unique)"}</div>
                <input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value })} style={{ ...css.input, direction: "ltr", fontFamily: "SF Mono, ui-monospace, monospace" }} placeholder="refund_10_percent" disabled={!!editing.id} data-testid="policy-key" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={css.fieldLbl}>{lang === "ar" ? "العنوان بالعربي *" : "Arabic title *"}</div>
                  <input value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} style={css.input} data-testid="policy-title-ar" />
                </div>
                <div>
                  <div style={css.fieldLbl}>{lang === "ar" ? "العنوان بالإنجليزي" : "English title"}</div>
                  <input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} style={{ ...css.input, direction: "ltr" }} data-testid="policy-title-en" />
                </div>
              </div>
              <div>
                <div style={css.fieldLbl}>{lang === "ar" ? "المحتوى بالعربي *" : "Arabic body *"}</div>
                <textarea value={editing.body_ar} onChange={(e) => setEditing({ ...editing, body_ar: e.target.value })} style={css.textarea} placeholder={T.body_ph} data-testid="policy-body-ar" />
              </div>
              <div>
                <div style={css.fieldLbl}>{lang === "ar" ? "المحتوى بالإنجليزي" : "English body"}</div>
                <textarea value={editing.body_en} onChange={(e) => setEditing({ ...editing, body_en: e.target.value })} style={{ ...css.textarea, direction: "ltr" }} data-testid="policy-body-en" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <div style={css.fieldLbl}>{lang === "ar" ? "النوع" : "Tone"}</div>
                  <select value={editing.tone} onChange={(e) => setEditing({ ...editing, tone: e.target.value })} style={css.input} data-testid="policy-tone">
                    {Object.entries(TONE_LABELS).map(([k, v]) => <option key={k} value={k}>{v[lang]}</option>)}
                  </select>
                </div>
                <div>
                  <div style={css.fieldLbl}>{lang === "ar" ? "الأولوية" : "Priority"}</div>
                  <input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: parseInt(e.target.value, 10) || 100 })} style={css.input} data-testid="policy-priority" />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="policy-active" />
                    {lang === "ar" ? "مفعّلة" : "Active"}
                  </label>
                </div>
              </div>
              <div>
                <div style={css.fieldLbl}>{lang === "ar" ? "أماكن العرض" : "Show in"}</div>
                <div style={css.locGrid}>
                  {Object.entries(LOCATION_LABELS).map(([k, v]) => {
                    const on = editing.locations.includes(k);
                    return (
                      <button
                        type="button"
                        key={k}
                        onClick={() => setEditing({
                          ...editing,
                          locations: on ? editing.locations.filter((x) => x !== k) : [...editing.locations, k],
                        })}
                        style={css.locOpt(on)}
                        data-testid={`policy-loc-${k}`}
                      >
                        {v[lang]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={css.footer}>
              <button type="button" onClick={() => setEditing(null)} style={css.backBtn} disabled={busy}>
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={save}
                style={css.addBtn}
                disabled={busy}
                data-testid="policy-save"
              >
                <Save size={16} strokeWidth={2.2} />
                {lang === "ar" ? "حفظ" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
